<template>
  <div v-if="report">
    <page-header class="mt-6 lg:mt-0" :full-width="true">
      <div class="flex flex-wrap">
        <div class="w-1/2 px-2">
          <h1 slot="header" class="text-grey-darker text-xl font-bold leading-normal py-4">
            {{report.title}} - {{properties.length}} Properties</h1>
          <div>Create Date: {{report.created_at | date}}</div>
        </div>
        <div class="flex w-1/2 pr-4 justify-end items-end">
          <button type="button"
                  class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                  @click="backToReport()"
          ><i class="fas fa-home"></i> Back to reports</button>
          <button type="button"
                  class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                  @click="changeView(true)"
          ><i class="fas fa-list"></i></button>
          <button type="button"
                  class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2"
                  @click="changeView(false)"
          >
            <i class="fas fa-image"></i>
          </button>
        </div>
      </div>
    </page-header>

    <panel class="mt-5 pt-6" v-if="!loading && properties && properties.length > 0">
      <div class="w-full max-w-full overflow-x-auto overflow-y-hidden">
        <table class="table-auto text-center w-full" v-if="briefView">
          <thead class="uppercase text-xs">
          <tr>
            <th class="text-left p-2 whitespace-no-wrap" colspan="2">
              Property
            </th>
            <th class="p-2 whitespace-no-wrap">
              Type
            </th>
            <th class="p-2 whitespace-no-wrap">
              Listed
            </th>
            <th class="p-2 whitespace-no-wrap">
              Status
            </th>
            <th class="p-2 whitespace-no-wrap">
              Graded
            </th>
            <th class="p-2 whitespace-no-wrap">
              Land Size
            </th>
            <th class="p-2 whitespace-no-wrap">
              Land Size Unit
            </th>
            <th class="p-2 whitespace-no-wrap">
              Floor Size
            </th>
            <th class="p-2 whitespace-no-wrap">
              Floor Size Unit
            </th>
            <th class="p-2 whitespace-no-wrap">
              Price per sq unit
            </th>
            <th class="p-2 whitespace-no-wrap">
              Condition
            </th>
          </tr>
          </thead>
          <tbody class="text-sm">
          <tr v-for="(property, i) in properties" :key="i"
              class="border-t border-grey-lighter hover:bg-grey-lightest">
            <td>
              <div class="icon-img w-24 h-16 my-2 rounded"
                   :style="{'background-image': `url('${property.main_photo}')`}" v-if="property.main_photo"></div>
              <div v-else class="relative w-24 h-16 px-6 pt-2 my-2 rounded bg-tertiary">
                <icon name="property_cms" class="text-white"/>
              </div>
            </td>
            <td class="text-left p-2 font-bold">{{ property.address }}</td>
            <td class="p-2">{{ property.type }}</td>
            <td class="p-2"><div v-if="property.listed_at">{{ property.listed_at | date }} <br> {{ property.listed_price }}</div></td>
            <td class="p-2">{{ property.status }}</td>
            <td class="p-2">{{ property.graded }} </td>
            <td class="p-2">{{ property.land_size }}</td>
            <td class="p-2">{{ property.land_size_unit_val }}</td>
            <td class="p-2">{{ property.floor_size }}</td>
            <td class="p-2">{{ property.floor_size_unit_val }}</td>
            <td class="p-2"><div v-if="property.price_sq">{{ parseFloat(property.price_sq) | gbp }}</div></td>
            <td class="p-2">{{ property.condition }}</td>
          </tr>
          </tbody>
        </table>
        <div class="flex flex-wrap" v-else>
          <div v-for="(property, i) in properties" :key="i"
               class="flex w-full border-t border-grey-lighter p-4">
            <div class="lg:w-1/3 px-8">
              <div v-if="property.media.images">
                <div class="slider-item" v-for="(image, j) in property.media.images" :key="j" v-if="j < 3" v-show="activeIndex == j">
                  <div class="flex items-center w-full h-64 overflow-hidden">
                    <img class="border" :src="property.media.images[j]['full_url']">
                  </div>
                </div>
                <div class="flex items-center text-center justify-center">
                  <span class="p-2" v-for="(image, k) in property.media.images" :key="k" v-if="k < 3" :class="[ k == activeIndex ? 'text-secondary' : 'text-grey-light' ]" @click="activeIndex = k">
                    <i class="fas fa-circle"></i>
                  </span>
                </div>
              </div>
              <div v-else-if="property.main_photo">
                <img class="icon-img w-24 h-16 my-2 rounded"
                     :src="`url('${property.main_photo}'`" >
              </div>
              <div v-else class="relative w-24 h-16 px-6 pt-2 my-2 rounded bg-tertiary">
                <icon name="property_cms" class="text-white"/>
              </div>
            </div>
            <div class="flex flex-wrap lg:w-2/3">
                <div class="w-full text-lg py-2"> {{ property.address }}</div>
                <div class="flex w-full text-xs py-2">
                  <span class="pr-8"><i class="fas fa-bed pr-2"></i> {{ property.bedrooms }}</span>
                  <span class="pr-8"><i class="fas fa-bath pr-2"></i> {{ property.bathrooms }}</span>
                  <span class="pr-8"><i class="fas fa-couch pr-2"></i> {{ property.reception_rooms }}</span>
                </div>
                <div class="flex w-full py-2">
                  <div class="flex flex-wrap w-1/2">
                    <div class="my-2 w-full">Listed Price: {{ property.listed_price }}</div>
                    <div class="my-2 w-full">Listed Date: {{property.listed_at | date}}</div>
                    <div class="my-2 w-full">Type: {{ property.type }}</div>
                    <div class="my-2 w-full">Graded: {{ property.graded ? property.graded : ' -' }}</div>
                    <div class="my-2 w-full">Status: {{property.status}}</div>
                    <div class="my-2 w-full">Condition: {{ property.condition }}</div>
                  </div>
                  <div class="flex flex-wrap w-1/2">
                    <div class="my-2 w-full">Land Size: {{ property.land_size ? property.land_size : ' -' }}</div>
                    <div class="my-2 w-full">Land Size Unit: {{ property.land_size_unit_val ? property.land_size_unit_val : ' -' }}</div>
                    <div class="my-2 w-full">Floor Size: {{ property.floor_size ? property.floor_size : ' -' }}</div>
                    <div class="my-2 w-full">Floor Size Unit: {{ property.floor_size_unit_val ? property.floor_size_unit_val : ' -' }}</div>
                    <div class="my-2 w-full">Price per sq unit: <span v-if="property.price_sq">{{ parseFloat(property.price_sq) | gbp}}</span><span v-else> -</span></div>
                  </div>
                </div>
            </div>
          </div>

        </div>
      </div>
    </panel>

    <panel v-if="!loading && properties && properties.length === 0" class="mt-5 pt-6">
      <strong class="mb-2">No Properties</strong>
      <p>Your search returned no results</p>
    </panel>
    <div v-if="loading" class="w-full h-screen text-center">
      <loader></loader>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import api from '@/http'

export default {
  name: 'PropertyComparisonReport',
  props: {
    selectedReportId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      activeIndex: 0,
      loading: false,
      properties: [],
      report: null,
      briefView: true
    }
  },
  computed: {
    ...mapState(['system', 'selectedOffice'])
  },
  methods: {
    ...mapActions(['displayToastNotification', 'setPageLoading']),
    goToProperty (id) {
      this.$router.push(`/property-management/${id}`)
    },
    getReport (reportId) {
      this.loading = true
      api.get(`/properties-comparison-report/previous-report/${reportId}`).then((response) => {
        this.loading = false
        this.report = response.data.report
        this.properties = response.data.properties
        this.loading = false
      }, () => {
        // Error
        this.loading = false
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Not able to load the report.'
        })
      })
    },
    changeView (flag) {
      this.briefView = flag
    },
    backToReport () {
      this.$emit('updateComponentVisibility')
    }
  },
  created () {
    this.getReport(this.selectedReportId)
  }
}
</script>
<style scoped>
  .icon-img {
    background-size: cover;
    background-position: center;
  }

  .icon {
    width: 80px;
    height: 60px;
  }
</style>
